import React, { useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { usePortalNavigate } from '@/hooks/usePortalPath';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@/components';
import { Colors } from '@core/config';
import CustomDateRangeStyle from './CustomDateRangeStyle';
import { useUserStore } from '@core/user/useUserStore';
import { useThemeStore } from '@core/theme/useThemeStore';
import DateRangePicker from '@/components/DateRangePicker';

interface HeaderProps {
  startDate: Dayjs | undefined;
  endDate: Dayjs | undefined;
  handleDateChange: (startDate: Dayjs, endDate: Dayjs) => void;
  refreshData: () => void;
}

const Header = ({
  startDate,
  endDate,
  handleDateChange,
  refreshData,
}: HeaderProps) => {
  const { currentUser } = useUserStore();

  const navigate = usePortalNavigate();

  const theme = useThemeStore();
  const dateRangeValue = useMemo(
    () => ({
      start: startDate?.toDate() ?? new Date(),
      end: endDate?.toDate() ?? new Date(),
    }),
    [startDate, endDate],
  );

  return (
    <div className="mb-4 flex items-end justify-between gap-2">
      <CustomDateRangeStyle />
      <div className="">
        <div className="mb-2 text-2xl font-semibold text-gray-800">
          {currentUser?.firstName
            ? `Hi, ${currentUser?.firstName}!`
            : 'Dashboard'}
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          {dayjs().format('dddd, MMM D, YYYY')}

          <div className="flex items-center">
            <FontAwesomeIcon
              style={{
                cursor: 'pointer',
                color: theme?.theme?.primaryBrandColor || Colors.LINK_BLUE,
              }}
              onClick={refreshData}
              icon={faArrowsRotate}
              className="mr-1"
            />{' '}
            <span>Last updated {dayjs().format('h:mm A')}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <DateRangePicker
          value={dateRangeValue}
          onChange={(start, end) => handleDateChange(dayjs(start), dayjs(end))}
          placeholder="Select date"
          primaryBrandColor={
            theme?.theme?.primaryBrandColor || Colors.LINK_BLUE
          }
          panelMargin="mt-4"
        />
        <Button
          variant="primary"
          className="bg-accent-900 hover:bg-accent-700 cursor-pointer px-3 py-1 text-sm"
          onClick={() =>
            navigate('/jobs/new', { state: { from: 'dashboard' } })
          }
        >
          + Post a job
        </Button>
      </div>
    </div>
  );
};

export default Header;
